/**
 * OnboardingPage — Value-First Acquisition Flow.
 *
 * A public, no-auth-required onboarding that lets a visitor build a
 * lightweight "Brand Brain" preview before ever creating an account.
 *
 * Flow:
 *   1. Welcome           — set the frame, single "Begin" CTA.
 *   2. Business details  — Business Name, Industry, Website, Logo, Goals.
 *   3. Building…         — animated synthesis (client-side only, no backend).
 *   4. Executive Snapshot — the preview of what Orkexra derived.
 *   5. Create account    — save this Brand Brain to a real workspace.
 *
 * By the time we ask for an account, the visitor has already seen value.
 * All state is client-side and persisted in localStorage so a refresh
 * during onboarding does not lose progress.
 */
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Brain, Building2, Check, ChevronLeft, ChevronRight, FileText,
  Globe2, Loader2, Palette, Sparkles, Target, Upload, Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { captureWaitlist } from "@/shared/api/waitlist";
import { useAuthStore } from "@/entities/auth/store";
import {
  clearDemoDraft,
  personalizeFromDraft,
  readDemoDraft,
} from "@/shared/lib/demo-personalize";

const STORAGE_KEY = "mos.onboarding.draft.v1";

type BusinessProfile = {
  businessName: string;
  industry: string;
  website: string;
  logoDataUrl: string | null;
  goals: string[];
  /** Raw description carried over from the /demo textarea (or typed in Step 2).
   *  Always preserved even when we can't infer a business name from it. */
  description: string;
};

const INDUSTRIES = [
  "E-commerce & Retail",
  "SaaS & Software",
  "Professional Services",
  "Health & Wellness",
  "Food & Beverage",
  "Real Estate",
  "Local Services",
  "Media & Content",
  "Other",
];

const GOALS = [
  { key: "brand-consistency", label: "Consistent brand voice everywhere" },
  { key: "content-velocity",  label: "Ship more content, faster" },
  { key: "campaign-ops",      label: "Run coordinated campaigns" },
  { key: "seo-visibility",    label: "Grow organic search & SEO" },
  { key: "social-presence",   label: "Build a real social presence" },
  { key: "lead-generation",   label: "Generate qualified leads" },
];

/* ── Aurora background reused visually — self-contained here ──────── */
function OnboardingBackground(): JSX.Element {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div
        className="absolute -left-40 -top-40 h-[600px] w-[600px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(229,180,84,0.12), transparent 60%)" }}
      />
      <div
        className="absolute -right-40 top-1/3 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(74,222,128,0.08), transparent 60%)" }}
      />
      <svg className="absolute inset-0 h-full w-full opacity-[0.035]">
        <filter id="ob-n"><feTurbulence baseFrequency="0.9" numOctaves="2" /></filter>
        <rect width="100%" height="100%" filter="url(#ob-n)" />
      </svg>
    </div>
  );
}

/* ── Top navigation strip ────────────────────────────────────────── */
function OnboardingNav({ step, total }: { step: number; total: number }): JSX.Element {
  const pct = Math.round((step / total) * 100);
  return (
    <header className="sticky top-0 z-30 border-b border-[color:var(--md-line)] backdrop-blur-xl"
            style={{ background: "linear-gradient(180deg, rgba(15,18,24,0.9), rgba(15,18,24,0.55))" }}>
      <div className="mx-auto flex max-w-[1080px] items-center gap-6 px-6 py-4">
        <Link to="/welcome" className="flex items-center gap-3">
          <span aria-hidden className="grid h-9 w-9 place-items-center rounded-[10px] font-display text-[16px] italic text-[var(--md-ink-0)]"
                style={{ background: "linear-gradient(135deg, var(--md-amber), #F5C15C)" }}>m</span>
          <span className="hidden flex-col leading-none sm:flex">
            <b className="orkexra-wordmark text-[13px]">ORKE<span className="x">X</span>RA</b>
            <span className="mt-1 md-eyebrow">Brand Brain Onboarding</span>
          </span>
        </Link>
        <div className="ml-auto flex flex-1 items-center gap-4">
          <div className="hidden flex-1 sm:block">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--md-ink-3)]">
              <motion.div
                className="h-full"
                style={{ background: "linear-gradient(90deg, var(--md-amber), #F5C15C)" }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.5, ease: [0.2, 0.7, 0.3, 1] }}
              />
            </div>
          </div>
          <span className="md-eyebrow whitespace-nowrap" data-testid="onboarding-progress">
            Step {step} of {total}
          </span>
        </div>
      </div>
    </header>
  );
}

/* ── Step 1 — Welcome ─────────────────────────────────────────────── */
function StepWelcome({ onNext }: { onNext: () => void }): JSX.Element {
  return (
    <motion.div
      key="welcome"
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-2xl text-center"
    >
      <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/10 px-3 py-1 md-eyebrow md-eyebrow--amber">
        <Sparkles className="h-3 w-3" /> Value-First Onboarding
      </div>
      <h1 className="mt-8 font-display italic tracking-tight text-[var(--md-ivory-0)] text-[52px] leading-[0.98] md:text-[76px]">
        Welcome to<br />
        <span style={{ color: "var(--md-amber)" }}>Orkexra</span>.
      </h1>
      <p className="mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-[var(--md-fog)]">
        In the next two minutes you&rsquo;ll answer five short questions. We&rsquo;ll
        synthesize your Brand Brain — the strategic core that will drive
        every campaign, every asset, every decision inside Orkexra.
      </p>
      <div className="mt-10 flex justify-center">
        <button
          type="button"
          onClick={onNext}
          data-testid="onboarding-begin"
          className="md-btn md-btn--primary"
          style={{ padding: "14px 28px", fontSize: 14 }}
        >
          Begin <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md-eyebrow">
        <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[var(--md-good)]" /> No credit card</span>
        <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[var(--md-good)]" /> No account required to preview</span>
        <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-[var(--md-good)]" /> Under two minutes</span>
      </div>
    </motion.div>
  );
}

/* ── Step 2 — Business details ────────────────────────────────────── */
function StepDetails({
  profile, setProfile, onBack, onNext,
}: {
  profile: BusinessProfile;
  setProfile: (p: BusinessProfile) => void;
  onBack: () => void;
  onNext: () => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canProceed = profile.businessName.trim().length > 1 && profile.industry !== "" && profile.goals.length > 0;

  const handleLogo = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setProfile({ ...profile, logoDataUrl: String(reader.result) });
    reader.readAsDataURL(file);
  };

  const toggleGoal = (key: string) => {
    const has = profile.goals.includes(key);
    setProfile({
      ...profile,
      goals: has ? profile.goals.filter((g) => g !== key) : [...profile.goals, key],
    });
  };

  return (
    <motion.div
      key="details"
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.4 }}
      className="mx-auto max-w-3xl"
    >
      <div className="mb-8 text-center">
        <div className="md-eyebrow md-eyebrow--amber">A few essentials</div>
        <h2 className="mt-3 font-display italic tracking-tight text-3xl text-[var(--md-ivory-0)] md:text-5xl">
          Tell us about your business.
        </h2>
        <p className="mt-4 text-[15px] text-[var(--md-fog)]">
          The more we know, the sharper your Brand Brain will be.
        </p>
      </div>

      <div className="md-panel rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-6 md:p-8">
        <div className="grid gap-6 md:grid-cols-2">
          {/* What you told the demo (or a fresh description) */}
          <label className="col-span-full flex flex-col gap-2">
            <span className="md-eyebrow flex items-center gap-1.5">
              <FileText className="h-3 w-3" /> What you do
              {profile.description.trim().length > 0 ? (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-[var(--md-amber)]">Carried over</span>
              ) : null}
            </span>
            <textarea
              rows={3}
              value={profile.description}
              onChange={(e) => setProfile({ ...profile, description: e.target.value })}
              data-testid="onboarding-description"
              placeholder="A few sentences about your business — who you serve and what you sell. This is exactly what you wrote on the demo, and you can edit it here."
              className="resize-y rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-4 py-3 text-[14px] leading-relaxed text-[var(--md-ivory-0)] placeholder:text-[var(--md-fog)] focus:border-[var(--md-amber)] focus:outline-none"
            />
          </label>

          {/* Business name */}
          <label className="col-span-full flex flex-col gap-2">
            <span className="md-eyebrow flex items-center gap-1.5"><Building2 className="h-3 w-3" /> Business name</span>
            <input
              type="text"
              value={profile.businessName}
              onChange={(e) => setProfile({ ...profile, businessName: e.target.value })}
              data-testid="onboarding-business-name"
              placeholder="Aurora Coffee Co."
              className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-4 py-3 text-[15px] text-[var(--md-ivory-0)] placeholder:text-[var(--md-fog)] focus:border-[var(--md-amber)] focus:outline-none"
            />
          </label>

          {/* Industry */}
          <label className="flex flex-col gap-2">
            <span className="md-eyebrow flex items-center gap-1.5"><Target className="h-3 w-3" /> Industry</span>
            <select
              value={profile.industry}
              onChange={(e) => setProfile({ ...profile, industry: e.target.value })}
              data-testid="onboarding-industry"
              className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-4 py-3 text-[15px] text-[var(--md-ivory-0)] focus:border-[var(--md-amber)] focus:outline-none"
            >
              <option value="">Select an industry…</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>

          {/* Website */}
          <label className="flex flex-col gap-2">
            <span className="md-eyebrow flex items-center gap-1.5"><Globe2 className="h-3 w-3" /> Website (optional)</span>
            <input
              type="url"
              value={profile.website}
              onChange={(e) => setProfile({ ...profile, website: e.target.value })}
              data-testid="onboarding-website"
              placeholder="https://your-brand.com"
              className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-4 py-3 text-[15px] text-[var(--md-ivory-0)] placeholder:text-[var(--md-fog)] focus:border-[var(--md-amber)] focus:outline-none"
            />
          </label>

          {/* Logo */}
          <div className="col-span-full flex flex-col gap-2">
            <span className="md-eyebrow flex items-center gap-1.5"><Palette className="h-3 w-3" /> Logo (optional)</span>
            <div className="flex items-center gap-4">
              <div
                aria-hidden
                className="grid h-16 w-16 flex-shrink-0 place-items-center overflow-hidden rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]"
              >
                {profile.logoDataUrl ? (
                  <img src={profile.logoDataUrl} alt="Logo preview" className="h-full w-full object-cover" />
                ) : (
                  <FileText className="h-5 w-5 text-[var(--md-fog)]" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleLogo(e.target.files?.[0])}
                data-testid="onboarding-logo-input"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="onboarding-logo-upload"
                className="md-btn md-btn--ghost"
                style={{ padding: "10px 16px", fontSize: 13 }}
              >
                <Upload className="h-3.5 w-3.5" /> {profile.logoDataUrl ? "Replace logo" : "Upload logo"}
              </button>
              {profile.logoDataUrl && (
                <button
                  type="button"
                  onClick={() => setProfile({ ...profile, logoDataUrl: null })}
                  className="text-xs text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"
                  data-testid="onboarding-logo-clear"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {/* Goals */}
          <div className="col-span-full flex flex-col gap-3">
            <span className="md-eyebrow flex items-center gap-1.5"><Zap className="h-3 w-3" /> What matters most? <span className="text-[var(--md-fog)]">(pick any)</span></span>
            <div className="grid gap-2 md:grid-cols-2">
              {GOALS.map((g) => {
                const active = profile.goals.includes(g.key);
                return (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => toggleGoal(g.key)}
                    data-testid={`onboarding-goal-${g.key}`}
                    className="flex items-center gap-3 rounded-lg border px-4 py-3 text-left text-[13px] transition"
                    style={{
                      borderColor: active ? "var(--md-amber)" : "var(--md-line)",
                      background: active ? "rgba(229,180,84,0.10)" : "var(--md-ink-2)",
                      color: active ? "var(--md-ivory-0)" : "var(--md-fog)",
                    }}
                  >
                    <span className="grid h-5 w-5 place-items-center rounded-md border"
                          style={{ borderColor: active ? "var(--md-amber)" : "var(--md-line)", background: active ? "var(--md-amber)" : "transparent" }}>
                      {active && <Check className="h-3 w-3 text-[var(--md-ink-0)]" />}
                    </span>
                    {g.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="md-btn md-btn--ghost" data-testid="onboarding-back-details">
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          data-testid="onboarding-continue-details"
          className="md-btn md-btn--primary disabled:cursor-not-allowed disabled:opacity-40"
          style={{ padding: "12px 24px", fontSize: 14 }}
        >
          Build my Brand Brain <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

/* ── Step 3 — Building animation ─────────────────────────────────── */
const BUILD_STEPS = [
  "Reading your business context…",
  "Distilling brand voice signals…",
  "Analyzing your industry landscape…",
  "Identifying competitive positioning…",
  "Mapping content pillars to your goals…",
  "Composing your Executive Snapshot…",
];

function StepBuilding({ onDone }: { onDone: () => void }): JSX.Element {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (idx >= BUILD_STEPS.length) {
      const t = setTimeout(onDone, 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIdx((i) => i + 1), 800);
    return () => clearTimeout(t);
  }, [idx, onDone]);

  return (
    <motion.div
      key="building"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="mx-auto max-w-xl text-center"
    >
      <div className="mx-auto mb-8 grid h-20 w-20 place-items-center rounded-full border border-[color:var(--md-amber)]/40"
           style={{ background: "radial-gradient(circle, rgba(229,180,84,0.25), transparent 70%)" }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 4, ease: "linear", repeat: Infinity }}
        >
          <Brain className="h-10 w-10 text-[var(--md-amber)]" />
        </motion.div>
      </div>

      <h2 className="font-display italic tracking-tight text-3xl text-[var(--md-ivory-0)] md:text-5xl">
        Building your <span style={{ color: "var(--md-amber)" }}>Brand Brain</span>…
      </h2>
      <p className="mt-4 text-[15px] text-[var(--md-fog)]">
        This is the strategic core every future campaign will reference.
      </p>

      <ul className="mt-10 space-y-3 text-left" data-testid="onboarding-build-steps">
        {BUILD_STEPS.map((s, i) => {
          const done = i < idx;
          const active = i === idx;
          return (
            <li key={s} className="flex items-center gap-3 rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-4 py-3">
              <span className="grid h-6 w-6 place-items-center rounded-full"
                    style={{
                      background: done ? "rgba(74,222,128,0.15)" : active ? "rgba(229,180,84,0.15)" : "var(--md-ink-3)",
                      border: `1px solid ${done ? "var(--md-good)" : active ? "var(--md-amber)" : "var(--md-line)"}`,
                    }}>
                {done ? (
                  <Check className="h-3 w-3 text-[var(--md-good)]" />
                ) : active ? (
                  <Loader2 className="h-3 w-3 animate-spin text-[var(--md-amber)]" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--md-fog)]" />
                )}
              </span>
              <span className="text-[13px]" style={{ color: done ? "var(--md-ivory-0)" : active ? "var(--md-ivory-0)" : "var(--md-fog)" }}>
                {s}
              </span>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}

/* ── Step 4 — Executive Snapshot ─────────────────────────────────── */
function StepSnapshot({
  profile, onBack, onNext,
}: {
  profile: BusinessProfile;
  onBack: () => void;
  onNext: () => void;
}): JSX.Element {
  const businessName = profile.businessName || "Your Brand";
  const industry = profile.industry || "Modern Business";

  // Derive a warm, plausible snapshot from the profile without any backend call.
  const voice = useMemo(() => {
    const g = profile.goals;
    if (g.includes("brand-consistency")) return "Confident, warm, precise — never salesy.";
    if (g.includes("content-velocity"))  return "Fast-cadence, quick-witted, always shipping.";
    if (g.includes("lead-generation"))   return "Direct, benefit-led, high signal.";
    return "Editorial, considered, human.";
  }, [profile.goals]);

  const pillars = useMemo(() => {
    const map: Record<string, string> = {
      "brand-consistency": "Signature brand moments",
      "content-velocity":  "Weekly content engine",
      "campaign-ops":      "Coordinated campaign arcs",
      "seo-visibility":    "SEO & organic authority",
      "social-presence":   "Social-native storytelling",
      "lead-generation":   "Conversion-focused offers",
    };
    const picked = profile.goals.map((g) => map[g]).filter(Boolean);
    if (picked.length === 0) return ["Signature brand moments", "Weekly content engine", "Coordinated campaign arcs"];
    return picked.slice(0, 4);
  }, [profile.goals]);

  const channels = ["Instagram", "LinkedIn", "Email", "SEO / Blog", "TikTok"];

  return (
    <motion.div
      key="snapshot"
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-4xl"
    >
      <div className="mb-8 text-center">
        <div className="md-eyebrow md-eyebrow--amber">Executive Snapshot</div>
        <h2 className="mt-3 font-display italic tracking-tight text-3xl text-[var(--md-ivory-0)] md:text-5xl">
          Meet your Brand Brain.
        </h2>
        <p className="mt-4 text-[15px] text-[var(--md-fog)]">
          Everything below is what Orkexra derived from your inputs. It only
          gets sharper once you connect real signals.
        </p>
      </div>

      <div
        data-testid="onboarding-snapshot"
        className="md-panel--glass rounded-2xl border border-[color:var(--md-amber)]/25 p-6 md:p-8"
        style={{ boxShadow: "0 40px 100px -40px rgba(0,0,0,0.6)" }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center gap-4 border-b border-[color:var(--md-line)] pb-6">
          <div className="grid h-14 w-14 flex-shrink-0 place-items-center overflow-hidden rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-2)]">
            {profile.logoDataUrl ? (
              <img src={profile.logoDataUrl} alt="Brand logo" className="h-full w-full object-cover" />
            ) : (
              <span className="font-display text-xl italic text-[var(--md-amber)]">
                {businessName.trim().charAt(0).toUpperCase() || "B"}
              </span>
            )}
          </div>
          <div className="flex-1">
            <div className="font-display italic text-2xl text-[var(--md-ivory-0)]">{businessName}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2 md-eyebrow">
              <span>{industry}</span>
              {profile.website && <><span>·</span><span className="truncate">{profile.website}</span></>}
            </div>
          </div>
          <span className="rounded-full border border-[color:var(--md-good)]/40 bg-[var(--md-good)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--md-good)]">
            Draft
          </span>
        </div>

        {/* Sections */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <SnapshotCard title="Brand voice">
            <p className="text-[14px] leading-relaxed text-[var(--md-ivory-0)]">{voice}</p>
          </SnapshotCard>

          <SnapshotCard title="Positioning">
            <p className="text-[14px] leading-relaxed text-[var(--md-ivory-0)]">
              A trusted <b>{industry.toLowerCase()}</b> brand that pairs
              distinctive product with an obsession for craft.
            </p>
          </SnapshotCard>

          <SnapshotCard title="Content pillars">
            <ul className="space-y-1.5 text-[14px] text-[var(--md-ivory-0)]">
              {pillars.map((p) => (
                <li key={p} className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--md-amber)]" /> {p}
                </li>
              ))}
            </ul>
          </SnapshotCard>

          <SnapshotCard title="Recommended channels">
            <div className="flex flex-wrap gap-2">
              {channels.map((c) => (
                <span key={c} className="rounded-full border border-[color:var(--md-line)] bg-[var(--md-ink-2)] px-3 py-1 text-[12px] text-[var(--md-ivory-0)]">
                  {c}
                </span>
              ))}
            </div>
          </SnapshotCard>

          <SnapshotCard title="First 30-day plan" wide>
            <ol className="grid gap-2 text-[13px] text-[var(--md-ivory-0)] md:grid-cols-3">
              {[
                { w: "Week 1", t: "Publish brand-story piece + refresh bios everywhere." },
                { w: "Week 2", t: "Ship 3 signature posts anchored to your pillars." },
                { w: "Week 3", t: "Launch a 5-email nurture sequence for warm leads." },
                { w: "Week 4", t: "Run a lightweight campaign against your top goal." },
              ].map((item) => (
                <li key={item.w} className="rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)] p-3">
                  <div className="md-eyebrow md-eyebrow--amber">{item.w}</div>
                  <div className="mt-1 leading-snug">{item.t}</div>
                </li>
              ))}
            </ol>
          </SnapshotCard>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="md-btn md-btn--ghost" data-testid="onboarding-back-snapshot">
          <ChevronLeft className="h-3.5 w-3.5" /> Refine inputs
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="onboarding-continue-snapshot"
          className="md-btn md-btn--primary"
          style={{ padding: "12px 24px", fontSize: 14 }}
        >
          Continue <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

function SnapshotCard({ title, children, wide }: { title: string; children: React.ReactNode; wide?: boolean }): JSX.Element {
  return (
    <div className={`rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-5 ${wide ? "md:col-span-2" : ""}`}>
      <div className="md-eyebrow md-eyebrow--amber">{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

/* ── Step 5 — Save / Create Account ─────────────────────────────── */
function StepSave({
  profile, onBack,
}: {
  profile: BusinessProfile;
  onBack: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const grantGuestSession = useAuthStore((s) => s.grantGuestSession);
  const [action, setAction] = useState<"enter" | "save" | null>(null);

  const persistDraft = () => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...profile, savedAt: new Date().toISOString() }),
      );
    } catch { /* localStorage may be unavailable */ }
    // Silently capture the lead — never blocks the user.
    void captureWaitlist({
      source: "onboarding",
      business_name: profile.businessName || undefined,
      industry: profile.industry || undefined,
      website: profile.website || undefined,
      goals: profile.goals,
    });
  };

  const enterWorkspaceAsGuest = () => {
    setAction("enter");
    persistDraft();
    grantGuestSession({
      businessName: profile.businessName || undefined,
      brandBrainDraftKey: STORAGE_KEY,
    });
    // Post-Harmony: guests land on the new operational Home. GuestBanner
    // explains status. AuthGuard whitelists /home for guest sessions.
    window.setTimeout(() => navigate("/home"), 350);
  };

  const goRegister = () => {
    setAction("save");
    persistDraft();
    window.setTimeout(() => navigate("/login?from=onboarding"), 350);
  };

  const businessName = profile.businessName || "your brand";
  const saving = action !== null;

  return (
    <motion.div
      key="save"
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5 }}
      className="mx-auto max-w-2xl text-center"
    >
      {/* Celebration halo */}
      <div className="relative mx-auto mb-6 h-20 w-20">
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(74,222,128,0.35), transparent 70%)" }}
          animate={{ scale: [0.85, 1.15, 1], opacity: [0, 1, 0.85] }}
          transition={{ duration: 1.2, ease: [0.2, 0.7, 0.3, 1] }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full border border-[color:var(--md-good)]/40"
          animate={{ scale: [1, 1.6, 1.9], opacity: [0.6, 0.15, 0] }}
          transition={{ duration: 1.6, ease: "easeOut", repeat: Infinity, repeatDelay: 1.2 }}
        />
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.7, 0.3, 1] }}
          className="absolute inset-0 grid place-items-center"
        >
          <div className="grid h-16 w-16 place-items-center rounded-full border border-[color:var(--md-good)]/50"
               style={{ background: "linear-gradient(180deg, rgba(74,222,128,0.14), rgba(74,222,128,0.04))" }}>
            <Check className="h-8 w-8 text-[var(--md-good)]" />
          </div>
        </motion.div>
      </div>

      <div className="md-eyebrow md-eyebrow--amber">Your Brand Brain is ready</div>
      <h2 className="mt-4 font-display italic tracking-tight text-3xl text-[var(--md-ivory-0)] md:text-5xl">
        Step into <span style={{ color: "var(--md-amber)" }}>{businessName}</span>.
      </h2>
      <p className="mx-auto mt-5 max-w-lg text-[15px] text-[var(--md-fog)]">
        Your personalized Orkexra workspace is built and waiting. Save it to your own
        account whenever you&rsquo;re ready.
      </p>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={enterWorkspaceAsGuest}
          disabled={saving}
          data-testid="onboarding-enter-workspace"
          className="md-btn md-btn--primary disabled:opacity-60"
          style={{ padding: "14px 28px", fontSize: 14 }}
        >
          {action === "enter" ? "Opening your workspace…" : "Enter my workspace"} <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={goRegister}
          disabled={saving}
          data-testid="onboarding-save-account"
          className="md-btn md-btn--ghost disabled:opacity-60"
          style={{ padding: "14px 22px", fontSize: 14 }}
        >
          {action === "save" ? "Saving your Brand Brain…" : "Save with an account"}
        </button>
      </div>

      <div className="mt-4 text-center md-eyebrow">
        Guest preview · your Brand Brain is saved locally · no card required
      </div>

      <div className="mt-8 flex justify-center">
        <button type="button" onClick={onBack} className="md-eyebrow underline underline-offset-4 hover:text-[var(--md-ivory-0)]" data-testid="onboarding-back-save">
          ← Back to snapshot
        </button>
      </div>
    </motion.div>
  );
}

/* ── Main page shell ─────────────────────────────────────────────── */
export default function OnboardingPage(): JSX.Element {
  const [step, setStep] = useState(1);
  // Demo-to-onboarding continuity: if we saved profile data, use that.
  // Otherwise, seed the businessName from what the visitor described in
  // the /demo textarea (cz.demo.business_draft). This must never
  // overwrite something the visitor has already typed inside onboarding.
  const [prefilledFromDemo, setPrefilledFromDemo] = useState(false);
  // Track the raw demo description so we can offer a "clear remembered
  // draft" affordance without wiping the visitor's onboarding progress.
  const [demoDraft, setDemoDraft] = useState<string>(() => readDemoDraft());
  const [profile, setProfile] = useState<BusinessProfile>(() => {
    let stored: Partial<BusinessProfile> | null = null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) stored = JSON.parse(raw);
    } catch { /* ignore parse errors */ }

    // Read the demo draft. Even if we can't parse a business name out
    // of it, we still want to carry the raw description into onboarding
    // so nothing the visitor typed is lost.
    let demoRaw = "";
    try { demoRaw = readDemoDraft().trim(); } catch { /* noop */ }

    // If a prior onboarding draft exists, honor it verbatim — but
    // opportunistically fill in a missing `description` from the demo
    // so upgrading users don't lose that context.
    if (stored) {
      const merged: BusinessProfile = {
        businessName: stored.businessName ?? "",
        industry: stored.industry ?? "",
        website: stored.website ?? "",
        logoDataUrl: stored.logoDataUrl ?? null,
        goals: Array.isArray(stored.goals) ? stored.goals : [],
        description: stored.description ?? demoRaw ?? "",
      };
      // Only flag continuity if the description originally came from demo
      // AND the user hasn't already customised anything else.
      if (
        !stored.description
        && demoRaw
        && !merged.businessName
        && !merged.industry
        && !merged.website
      ) {
        setTimeout(() => setPrefilledFromDemo(true), 0);
      }
      return merged;
    }

    // No prior onboarding draft — seed from the demo description.
    if (demoRaw.length > 0) {
      const persona = personalizeFromDraft(demoRaw);
      const seedName =
        persona.hasDescription && persona.businessName !== "Your Business"
          ? persona.businessName
          : "";
      setTimeout(() => setPrefilledFromDemo(true), 0);
      return {
        businessName: seedName,
        industry: "",
        website: "",
        logoDataUrl: null,
        goals: [],
        description: demoRaw, // Always preserve the raw text.
      };
    }

    return { businessName: "", industry: "", website: "", logoDataUrl: null, goals: [], description: "" };
  });

  // Persist as they go (auto-save). Never overwrites the demo draft —
  // they live in separate localStorage keys by design.
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile)); } catch { /* noop */ }
  }, [profile]);

  const handleClearDemoDraft = () => {
    // Only removes the demo textarea memory; the onboarding profile
    // itself (name/industry/goals/logo) is preserved so the visitor
    // never loses their onboarding progress.
    clearDemoDraft();
    setDemoDraft("");
    setPrefilledFromDemo(false);
  };

  const totalSteps = 5;

  return (
    <div data-testid="onboarding-page" className="relative min-h-screen text-[var(--md-ivory-0)]">
      <OnboardingBackground />
      <OnboardingNav step={step} total={totalSteps} />
      {prefilledFromDemo && demoDraft.trim().length > 0 ? (
        <div
          data-testid="onboarding-demo-continuity"
          className="mx-auto mt-4 flex max-w-2xl flex-wrap items-center justify-center gap-2 px-6 text-center text-xs text-[var(--md-fog)]"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/5 px-3 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--md-amber)]" />
            We remembered what you shared in the demo.
          </span>
          <button
            type="button"
            onClick={handleClearDemoDraft}
            data-testid="onboarding-demo-clear"
            className="rounded border border-[color:var(--md-line)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--md-fog)] transition hover:border-[color:var(--md-amber)]/40 hover:text-[var(--md-ivory-0)]"
          >
            Clear remembered draft
          </button>
        </div>
      ) : null}
      <main className="px-6 pb-24 pt-14 md:pt-20">
        <AnimatePresence mode="wait">
          {step === 1 && <StepWelcome onNext={() => setStep(2)} />}
          {step === 2 && <StepDetails profile={profile} setProfile={setProfile} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
          {step === 3 && <StepBuilding onDone={() => setStep(4)} />}
          {step === 4 && <StepSnapshot profile={profile} onBack={() => setStep(2)} onNext={() => setStep(5)} />}
          {step === 5 && <StepSave profile={profile} onBack={() => setStep(4)} />}
        </AnimatePresence>
      </main>
    </div>
  );
}
